import { useTranslations } from '@telialt/common-cms/src/useTranslations';
import { FilterRange, FilterTerms, SearchFacet, SearchFilters } from '@telialt/core-search/src/model';
import { usePathname, useSearchParams } from '@telialt/utils/src/pathname';

import { FACET_QUERY_KEY_MAP, HIDE_FILTERS, IGNORE_FILTERS } from './constants';
import {
    FacetFilteringControls,
    FacetQueryKeyMap,
    FilterChangeHandler,
    FilterRemoveAllHandler,
    FilterRemoveHandler,
    ParamReadyFacetFilter,
    ParamReadyStats,
    ParamReadyTerms,
} from './model';
import { toRangeParam, toRangeValue, toSearchFilters, toTermsParam, toTermsValue } from './transformers';

type FacetFilteringControlsProps = {
    facets: SearchFacet[];
    initialFilters?: SearchFilters;
    searchParams?: { [key: string]: string | undefined };
};
export const useFacetFilteringControls = ({
    facets = [],
    initialFilters = {},
    searchParams,
}: FacetFilteringControlsProps): FacetFilteringControls => {
    const { t } = useTranslations();
    const facetQueryKeyMap = Object.fromEntries(
        Object.entries(FACET_QUERY_KEY_MAP).map(([key, messageID]) => [key, t[messageID]]),
    ) as FacetQueryKeyMap;

    const locationPath = usePathname();
    const query = useSearchParams(searchParams);

    const paramReadyFacetsFilters: ParamReadyFacetFilter[] = facets
        .filter(({ key }) => !IGNORE_FILTERS.includes(key))
        .map((facet) => {
            const paramKey = facetQueryKeyMap[facet.key];
            const paramValue = query.get(paramKey);
            const initialFilter = initialFilters[facet.key];

            switch (facet.type) {
                case 'stats': {
                    const defaultParamValue = toRangeParam(Math.floor(facet.min), Math.ceil(facet.max));

                    return {
                        ...facet,
                        initialValue: initialFilter as FilterRange,
                        value: toRangeValue(paramValue),
                        paramKey,
                        paramValue,
                        paramValueDefault: defaultParamValue,
                        setParamValue(newValue: FilterRange) {
                            const newParamValue = newValue && toRangeParam(newValue.gte, newValue.lte);
                            if (newParamValue && newParamValue !== defaultParamValue) {
                                query.set(paramKey, newParamValue);
                            } else {
                                query.delete(paramKey);
                            }

                            return query.toString();
                        },
                    } satisfies ParamReadyStats;
                }
                case 'terms': {
                    return {
                        ...facet,
                        initialValue: initialFilter as FilterTerms,
                        value: toTermsValue(paramValue),
                        paramKey,
                        paramValue,
                        paramValueDefault: undefined,
                        setParamValue(newValue: FilterTerms) {
                            const newParamValue = toTermsParam(newValue);
                            if (newParamValue) {
                                query.set(paramKey, newParamValue);
                            } else {
                                query.delete(paramKey);
                            }

                            return query.toString();
                        },
                    } satisfies ParamReadyTerms;
                }
                default:
                    return null;
            }
        })
        .filter(Boolean);

    const pushHistoryState = (newQueryString?: string) => {
        const url = newQueryString ? `${locationPath}?${newQueryString}` : locationPath;
        window.history.pushState(window.history.state, '', url);
        window.dispatchEvent(new CustomEvent('popstate'));
    };

    const updateFilter = (fieldName: string, value: any) => {
        const filter = paramReadyFacetsFilters.find(({ key }) => key === fieldName);
        const newQuery = filter.setParamValue(value);
        pushHistoryState(newQuery);
    };

    const handleFilterChange: FilterChangeHandler = updateFilter;
    const handleRemoveFilter: FilterRemoveHandler = updateFilter;
    const handleRemoveAllFilters: FilterRemoveAllHandler = () => {
        paramReadyFacetsFilters.forEach((filter) => query.delete(filter.paramKey));
        pushHistoryState(query.toString());
    };

    const filters = { ...initialFilters, ...toSearchFilters(paramReadyFacetsFilters) };

    const appliedFilters = toSearchFilters(
        paramReadyFacetsFilters.filter(
            (filter) => !(HIDE_FILTERS.includes(filter.key) || filter.paramValue === filter.paramValueDefault),
        ),
    );

    return {
        facets: paramReadyFacetsFilters,
        filters,
        appliedFilters,
        onFilterChange: handleFilterChange,
        onRemoveAllFilters: handleRemoveAllFilters,
        onRemoveFilter: handleRemoveFilter,
    };
};
